// src/components/AdminPanel/CandidateTable.js
import React, { useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import LoadingAnimation from "../LoadingAnimation";
import { db } from "../../firebase";
import {
  doc,
  deleteDoc,
  updateDoc,
  serverTimestamp,
  collection,
  onSnapshot,
  orderBy,
  query,
  getDocs,
  where,
  getDoc,
  addDoc,
} from "firebase/firestore";
import { toast } from "react-toastify";
import emailjs from "emailjs-com";

// ─── REJECTION EMAIL (sent by admin when candidate is rejected) ────────────────
function buildRejectionEmail(data, jobTitle, emailLogo) {
  const year = new Date().getFullYear();
  const logoUrl = emailLogo || "https://i.postimg.cc/023xvzzm/vbt-logo-2.png";

  return (
    "<table width='100%' cellpadding='0' cellspacing='0' style='background:#f0f4f8;padding:36px 0;font-family:Segoe UI,Arial,sans-serif;'>" +
    "<tr><td align='center'>" +
    "<table width='600' cellpadding='0' cellspacing='0' style='background:#ffffff;border-radius:16px;overflow:hidden;'>" +

    // HEADER
    "<tr>" +
    "<td style='background:#ff8000;text-align:center;padding:36px 24px 30px;'>" +
    "<img src='" + logoUrl + "' width='160px' alt='VBT' style='display:block;margin:0 auto 18px;' />" +
    "<h1 style='margin:0;color:#ffffff;font-size:24px;font-weight:700;'>Application Update</h1>" +
    "</td>" +
    "</tr>" +

    // BODY
    "<tr>" +
    "<td style='padding:32px 32px 24px;'>" +
    "<p style='margin:0 0 16px;font-size:16px;color:#222;line-height:1.7;'>Dear <strong style='color:#ff6600;'>" + data.name + "</strong>,</p>" +
    "<p style='margin:0 0 16px;font-size:14px;color:#555;line-height:1.8;'>" +
    "Thank you for taking the time to apply for the <strong>" + jobTitle + "</strong> position at VisionBird Technologies. We truly appreciate your interest in joining our team." +
    "</p>" +
    "<p style='margin:0 0 16px;font-size:14px;color:#555;line-height:1.8;'>" +
    "We received a wonderful response for this role, and unfortunately, we have already filled all our available seats with other candidates. Please know that our decision was simply a matter of limited capacity rather than a reflection of your talent or potential." +
    "</p>" +
    "<p style='margin:0 0 16px;font-size:14px;color:#555;line-height:1.8;'>" +
    "We encourage you to keep developing your skills and to apply again in the future when new opportunities open up. We are very grateful for your effort and wish you wonderful success in your career journey." +
    "</p>" +
    "</td>" +
    "</tr>" +

    // CLOSING
    "<tr>" +
    "<td style='padding:0 32px 28px;'>" +
    "<p style='margin:0;font-size:14px;color:#555;line-height:1.8;'>" +
    "We wish you the best in your job search." +
    "</p>" +
    "<p style='margin:14px 0 0;font-size:14px;color:#333;'>" +
    "Warm Regards,<br /><strong>VisionBird Technologies &mdash; HR Team</strong>" +
    "</p>" +
    "</td>" +
    "</tr>" +

    // FOOTER
    "<tr>" +
    "<td style='background:#111111;text-align:center;padding:22px;'>" +
    "<p style='margin:0 0 4px;color:#ff8000;font-size:13px;font-weight:700;letter-spacing:0.5px;'>VISIONBIRD TECHNOLOGIES</p>" +
    "<p style='margin:0;color:#666;font-size:12px;'>&#169; " + year + " &nbsp;&#183;&nbsp; Please do not reply to this email</p>" +
    "</td>" +
    "</tr>" +

    "</table>" +
    "</td></tr>" +
    "</table>"
  );
}

// ─── IRRELEVANT EXPERIENCE REJECTION EMAIL ────────────────────────────────────
function buildIrrelevantExperienceEmail(data, jobTitle, emailLogo) {
  const year = new Date().getFullYear();
  const logoUrl = emailLogo || "https://i.postimg.cc/023xvzzm/vbt-logo-2.png";

  return (
    "<table width='100%' cellpadding='0' cellspacing='0' style='background:#f0f4f8;padding:36px 0;font-family:Segoe UI,Arial,sans-serif;'>" +
    "<tr><td align='center'>" +
    "<table width='600' cellpadding='0' cellspacing='0' style='background:#ffffff;border-radius:16px;overflow:hidden;'>" +

    "<tr>" +
    "<td style='background:#ff8000;text-align:center;padding:36px 24px 30px;'>" +
    "<img src='" + logoUrl + "' width='160px' alt='VBT' style='display:block;margin:0 auto 18px;' />" +
    "<h1 style='margin:0;color:#ffffff;font-size:24px;font-weight:700;'>Thank You for Applying</h1>" +
    "</td>" +
    "</tr>" +

    "<tr>" +
    "<td style='padding:32px 32px 24px;'>" +
    "<p style='margin:0 0 16px;font-size:16px;color:#222;line-height:1.7;'>Dear <strong style='color:#ff6600;'>" + data.name + "</strong>,</p>" +
    "<p style='margin:0 0 16px;font-size:14px;color:#555;line-height:1.8;'>" +
    "Thank you for taking the time to apply for the <strong>" + jobTitle + "</strong> position at VisionBird Technologies. We sincerely appreciate your enthusiasm and the effort you put into your application." +
    "</p>" +
    "<p style='margin:0 0 16px;font-size:14px;color:#555;line-height:1.8;'>" +
    "After carefully reviewing your profile, we found that your current experience and background do not closely align with the specific skill set and expertise required for this particular role at this time. Please know that this decision was made with great respect and in no way reflects on your abilities or your potential as a professional." +
    "</p>" +
    "<p style='margin:0 0 16px;font-size:14px;color:#555;line-height:1.8;'>" +
    "Every great career is built step by step, and we strongly believe that with continued dedication, learning, and growth in the relevant areas, you will be an excellent fit for opportunities like this in the future. We genuinely encourage you to keep developing your skills and to revisit us when your experience more closely matches the requirements of the role." +
    "</p>" +
    "<p style='margin:0 0 16px;font-size:14px;color:#555;line-height:1.8;'>" +
    "We are truly grateful for your interest in VisionBird Technologies and we wish you the very best in your career journey ahead." +
    "</p>" +
    "</td>" +
    "</tr>" +

    "<tr>" +
    "<td style='padding:0 32px 28px;'>" +
    "<p style='margin:0;font-size:14px;color:#555;line-height:1.8;'>Keep going — your right opportunity is ahead of you.</p>" +
    "<p style='margin:14px 0 0;font-size:14px;color:#333;'>" +
    "Warm Regards,<br /><strong>VisionBird Technologies &mdash; HR Team</strong>" +
    "</p>" +
    "</td>" +
    "</tr>" +

    "<tr>" +
    "<td style='background:#111111;text-align:center;padding:22px;'>" +
    "<p style='margin:0 0 4px;color:#ff8000;font-size:13px;font-weight:700;letter-spacing:0.5px;'>VISIONBIRD TECHNOLOGIES</p>" +
    "<p style='margin:0;color:#666;font-size:12px;'>&#169; " + year + " &nbsp;&#183;&nbsp; Please do not reply to this email</p>" +
    "</td>" +
    "</tr>" +

    "</table>" +
    "</td></tr>" +
    "</table>"
  );
}

// ─── INTERVIEW INVITATION EMAIL (sent by admin via "Send Reply" button) ───────
// Sent ONLY when the admin selects a candidate and clicks "Send Reply".
// Communicates that the candidate has been shortlisted and their interview
// is confirmed — includes full interview details and the pre-interview checklist.
function buildInterviewInviteEmail(data, jobTitle, emailLogo) {
  const year = new Date().getFullYear();
  const logoUrl = emailLogo || "https://i.postimg.cc/023xvzzm/vbt-logo-2.png";

  const progressSteps = [
    { icon: "&#10003;", label: "Application<br>Received", active: true },
    { icon: "&#10003;", label: "Profile<br>Shortlisted", active: true },
    { icon: "&#127897;", label: "Interview<br>Scheduled", active: true },
  ];

  const progressCells = progressSteps
    .map(function (step, i) {
      const circle =
        "<div style='width:48px;height:48px;border-radius:50%;margin:0 auto 10px;" +
        "background:#ff8000;" +
        "line-height:48px;font-size:20px;text-align:center;color:#fff;'>" +
        step.icon +
        "</div>" +
        "<p style='margin:0;font-size:11px;font-weight:700;color:#ff6600;text-align:center;line-height:1.5;'>" +
        step.label +
        "</p>";

      const cell = "<td style='text-align:center;padding:0 4px;'>" + circle + "</td>";

      if (i < progressSteps.length - 1) {
        const connector = "<td style='padding-bottom:32px;'>" +
          "<div style='height:2px;background:#ffc680;margin-top:24px;'></div>" +
          "</td>";
        return cell + connector;
      }
      return cell;
    })
    .join("");

  const checklistItems = [
    { icon: "&#128336;", color: "#ff8000", bg: "#fff8f0", border: "#ffd199", title: "Be On Time", desc: "Please arrive at least <strong>10 minutes early</strong>. Late arrivals may not be accommodated." },
    { icon: "&#128196;", color: "#0055cc", bg: "#f0f6ff", border: "#b3d0ff", title: "Bring Your ID Card", desc: "Carry your original <strong>CNIC or any valid government-issued ID</strong> for verification." },
    { icon: "&#128187;", color: "#1a7a45", bg: "#f0fff6", border: "#99e8c0", title: "Bring Your Laptop", desc: "Bring a <strong>fully charged laptop</strong>. The assessment or practical task may take up to 1 hour, so please ensure your device is ready to use." },
  ];

  const checklistRows = checklistItems
    .map(function (item) {
      return (
        "<tr>" +
        "<td style='padding:6px 0;'>" +
        "<table width='100%' cellpadding='0' cellspacing='0' style='background:" + item.bg + ";border:1px solid " + item.border + ";border-radius:10px;margin-bottom:10px;'>" +
        "<tr>" +
        "<td style='padding:14px 16px;width:48px;text-align:center;font-size:24px;vertical-align:middle;'>" + item.icon + "</td>" +
        "<td style='padding:14px 16px 14px 0;vertical-align:middle;'>" +
        "<p style='margin:0 0 3px;font-weight:700;color:" + item.color + ";font-size:13px;'>" + item.title + "</p>" +
        "<p style='margin:0;color:#555;font-size:13px;line-height:1.6;'>" + item.desc + "</p>" +
        "</td>" +
        "</tr>" +
        "</table>" +
        "</td>" +
        "</tr>"
      );
    })
    .join("");

  return (
    "<table width='100%' cellpadding='0' cellspacing='0' style='background:#f0f4f8;padding:36px 0;font-family:Segoe UI,Arial,sans-serif;'>" +
    "<tr><td align='center'>" +
    "<table width='600' cellpadding='0' cellspacing='0' style='background:#ffffff;border-radius:16px;overflow:hidden;'>" +

    // HEADER
    "<tr>" +
    "<td style='background:#ff8000;text-align:center;padding:36px 24px 30px;'>" +
    "<img src='" + logoUrl + "' width='160px' alt='VBT' style='display:block;margin:0 auto 18px;' />" +
    "<h1 style='margin:0;color:#ffffff;font-size:24px;font-weight:700;'>You've Been Selected!</h1>" +
    "<p style='margin:8px 0 0;color:rgba(255,255,255,0.85);font-size:14px;'>Your interview at VisionBird Technologies is now confirmed</p>" +
    "</td>" +
    "</tr>" +

    // GREETING
    "<tr>" +
    "<td style='padding:32px 32px 16px;'>" +
    "<p style='margin:0;font-size:16px;color:#222;line-height:1.7;'>Hi <strong style='color:#ff6600;'>" + data.name + "</strong>,</p>" +
    "<p style='margin:10px 0 0;font-size:14px;color:#555;line-height:1.8;'>" +
    "Congratulations! After reviewing your application for <strong style='color:#111;'>" + jobTitle + "</strong> at " +
    "<strong style='color:#ff6600;'>VisionBird Technologies</strong>, " +
    "we are pleased to inform you that you have been <strong style='color:#ff6600;'>shortlisted for an interview</strong>. " +
    "Please find your confirmed interview details below." +
    "</p>" +
    "</td>" +
    "</tr>" +

    // INTERVIEW DETAILS
    "<tr>" +
    "<td style='padding:8px 32px 16px;'>" +
    "<table width='100%' cellpadding='0' cellspacing='0' style='background:#fff8f0;border:1px solid #ffd199;border-radius:12px;overflow:hidden;'>" +
    "<tr>" +
    "<td style='background:#ff8000;padding:12px 18px;'>" +
    "<p style='margin:0;color:#fff;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:1px;'>&#128197; &nbsp;Your Interview Details</p>" +
    "</td>" +
    "</tr>" +
    "<tr>" +
    "<td style='padding:16px 18px;'>" +
    "<table width='100%' cellpadding='0' cellspacing='0'>" +
    "<tr>" +
    "<td style='padding:7px 0;color:#888;font-size:13px;width:40%;'>Job Position</td>" +
    "<td style='padding:7px 0;color:#111;font-size:13px;font-weight:600;'>" + jobTitle + "</td>" +
    "</tr>" +
    "<tr>" +
    "<td style='padding:7px 0;color:#888;font-size:13px;border-top:1px solid #f5e6d0;'>Interview Date</td>" +
    "<td style='padding:7px 0;color:#111;font-size:13px;font-weight:600;border-top:1px solid #f5e6d0;'>" + (data.interviewDate ? formatNiceDate(data.interviewDate) : "To be communicated via call") + "</td>" +
    "</tr>" +
    "<tr>" +
    "<td style='padding:7px 0;color:#888;font-size:13px;border-top:1px solid #f5e6d0;'>Time Slot</td>" +
    "<td style='padding:7px 0;color:#111;font-size:13px;font-weight:600;border-top:1px solid #f5e6d0;'>" + (data.interviewSlot || "To be communicated via call") + "</td>" +
    "</tr>" +
    "</table>" +
    "</td>" +
    "</tr>" +
    "</table>" +
    "</td>" +
    "</tr>" +

    // PROGRESS STEPS
    "<tr>" +
    "<td style='padding:8px 32px 16px;'>" +
    "<table width='100%' cellpadding='0' cellspacing='0' style='background:#fafafa;border:1px solid #eeeeee;border-radius:12px;padding:20px 16px;'>" +
    "<tr><td>" +
    "<p style='margin:0 0 18px;text-align:center;font-size:11px;font-weight:700;color:#bbb;text-transform:uppercase;letter-spacing:1.2px;'>Application Progress</p>" +
    "<table width='100%' cellpadding='0' cellspacing='0'><tr>" +
    progressCells +
    "</tr></table>" +
    "</td></tr>" +
    "</table>" +
    "</td>" +
    "</tr>" +

    // INTERVIEW CHECKLIST
    "<tr>" +
    "<td style='padding:8px 32px 16px;'>" +
    "<p style='margin:0 0 12px;font-size:11px;font-weight:700;color:#999;text-transform:uppercase;letter-spacing:1.2px;'>Before You Arrive</p>" +
    "<table width='100%' cellpadding='0' cellspacing='0'>" +
    checklistRows +
    "</table>" +
    "</td>" +
    "</tr>" +

    // CLOSING
    "<tr>" +
    "<td style='padding:8px 32px 28px;border-top:1px solid #f5f5f5;margin-top:8px;'>" +
    "<p style='margin:0;font-size:14px;color:#555;line-height:1.8;'>We look forward to meeting you. Best of luck! &#127919;</p>" +
    "<p style='margin:14px 0 0;font-size:14px;color:#333;'>Warm regards,<br><strong style='color:#ff6600;'>VisionBird Technologies &mdash; HR Team</strong></p>" +
    "</td>" +
    "</tr>" +

    // FOOTER
    "<tr>" +
    "<td style='background:#111111;text-align:center;padding:22px;'>" +
    "<p style='margin:0 0 4px;color:#ff8000;font-size:13px;font-weight:700;letter-spacing:0.5px;'>VISIONBIRD TECHNOLOGIES</p>" +
    "<p style='margin:0;color:#666;font-size:12px;'>&#169; " + year + " &nbsp;&#183;&nbsp; Please do not reply to this email</p>" +
    "</td>" +
    "</tr>" +

    "</table>" +
    "</td></tr>" +
    "</table>"
  );
}

// ── Status normaliser ────────────────────────────────────────────────────────
const normalise = (raw) => {
  const s = (raw || "").toLowerCase().trim();
  if (s === "accepted" || s === "selected") return "accepted";
  if (s === "rejected") return "rejected";
  if (s === "blocked") return "blocked";
  if (s === "interviewing") return "interviewing";
  return "pending";
};

// ── Date Formatter ───────────────────────────────────────────────────────────
const formatNiceDate = (dateVal) => {
  if (!dateVal) return "N/A";
  let dateObj;

  if (typeof dateVal.toDate === "function") {
    dateObj = dateVal.toDate();
  } else if (typeof dateVal === "string") {
    const parts = dateVal.split("-");
    if (parts.length === 3 && parts[0].length === 2 && parts[2].length === 4) {
      // DD-MM-YYYY
      dateObj = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
    } else {
      dateObj = new Date(dateVal);
    }
  } else if (dateVal instanceof Date) {
    dateObj = dateVal;
  } else if (typeof dateVal === "number") {
    dateObj = new Date(dateVal);
  }

  if (!dateObj || isNaN(dateObj.getTime())) return typeof dateVal === "string" ? dateVal : "N/A";

  return dateObj.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
};

const STATUS_CONFIG = {
  pending: { label: "Pending", bg: "rgb(241,172,12)", color: "#fff" },
  accepted: { label: "Accepted", bg: "#21b11d", color: "#fff" },
  rejected: { label: "Rejected", bg: "#f56565", color: "#fff" },
  blocked: { label: "Blocked", bg: "#718096", color: "#fff" },
};

const CandidateTable = ({ activeTab }) => {
  const [allApplications, setAllApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });
  const [confirmDelete, setConfirmDelete] = useState({ visible: false, application: null });
  const [confirmAccept, setConfirmAccept] = useState({ visible: false, application: null });
  const [confirmReject, setConfirmReject] = useState({ visible: false, application: null });
  const [sendRejectNotify, setSendRejectNotify] = useState(true);
  const [rejectReason, setRejectReason] = useState("capacity");
  const [sendAcceptInvite, setSendAcceptInvite] = useState(true);
  const [confirmBulkAccept, setConfirmBulkAccept] = useState(false);
  const [confirmBulkReject, setConfirmBulkReject] = useState(false);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [viewResume, setViewResume] = useState({ visible: false, application: null });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [emailLogo, setEmailLogo] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const selectAllRef = useRef(null);

  // ── Fetch email logo from Firestore ─────────────────────────────────────
  useEffect(() => {
    emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY);
    const fetchLogo = async () => {
      try {
        const snap = await getDoc(doc(db, "logos", "xrSCDlcVzp10becG8bnW"));
        if (snap.exists()) setEmailLogo(snap.data().emailLogo || "");
      } catch (e) { console.error(e); }
    };
    fetchLogo();
  }, []);

  // ── Send interview invite email (called from handleConfirmAccept) ──────────
  const handleSendReply = async (application) => {
    const jobTitle = application.position || application.jobTitle || "N/A";
    const cleanTitle = jobTitle.replace(/&#x2F;/gi, "/");
    const cleanEmail = (application.email || "").trim();
    const params = {
      to_email: cleanEmail,
      email: cleanEmail,
      subject: "You've Been Selected for an Interview - " + cleanTitle + " | VisionBird Technologies",
      message: buildInterviewInviteEmail(application, jobTitle, emailLogo),
    };
    console.log("[CandidateTable] Sending interview invite:", {
      to: params.email,
      subject: params.title,
      service: process.env.REACT_APP_EMAILJS_SERVICE_ID,
      template: process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
    });
    const result = await emailjs.send(
      process.env.REACT_APP_EMAILJS_SERVICE_ID,
      process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
      params,
      process.env.REACT_APP_EMAILJS_PUBLIC_KEY
    );
    console.log("[CandidateTable] EmailJS response:", result);
    await updateDoc(doc(db, "jobApplications", application.id), { replySent: true });
  };

  // ── Send rejection email ──────────────────────────────────────────────────
  const handleSendRejection = async (application, reason = "capacity") => {
    const jobTitle = application.position || application.jobTitle || "N/A";
    const cleanTitle = jobTitle.replace(/&#x2F;/gi, "/");
    const cleanEmail = (application.email || "").trim();
    const message = reason === "experience"
      ? buildIrrelevantExperienceEmail(application, cleanTitle, emailLogo)
      : buildRejectionEmail(application, cleanTitle, emailLogo);
    const params = {
      to_email: cleanEmail,
      email: cleanEmail,
      subject: "Your Application for " + cleanTitle + " | VisionBird Technologies",
      message,
    };
    console.log("[CandidateTable] Sending rejection email:", {
      to: params.to_email,
      subject: params.subject,
      reason,
      service: process.env.REACT_APP_EMAILJS_SERVICE_ID,
      template: process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
    });
    const result = await emailjs.send(
      process.env.REACT_APP_EMAILJS_SERVICE_ID,
      process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
      params,
      process.env.REACT_APP_EMAILJS_PUBLIC_KEY
    );
    console.log("[CandidateTable] Rejection EmailJS response:", result);
  };

  // ── Reject: update status + send rejection email ─────────────────────────
  const handleConfirmReject = async () => {
    const { application } = confirmReject;
    if (!application) return;
    setConfirmReject({ visible: false, application: null });
    try {
      await updateDoc(doc(db, "jobApplications", application.id), {
        status: "rejected",
        statusUpdatedAt: serverTimestamp(),
      });
      toast.success(`${application.name} has been rejected.`);

      if (sendRejectNotify) {
        if (!application.email) {
          toast.warning("Status updated, but no email address was found to notify the candidate.");
          return;
        }
        await handleSendRejection(application, rejectReason);
        toast.info("Rejection notification sent to " + application.email + ".");
      }
    } catch (err) {
      console.error("[CandidateTable] Reject/email failed:", err);
      toast.error("Email failed: " + (err?.text || err?.message || JSON.stringify(err)));
    }
  };

  // ── Firestore realtime listener ──────────────────────────────────────────
  useEffect(() => {
    const q = query(collection(db, "jobApplications"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      setAllApplications(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const currentApplications = useMemo(() => {
    if (activeTab === "all") return allApplications.filter(app => normalise(app.status) !== "interviewing");
    return allApplications.filter((app) => normalise(app.status) === activeTab);
  }, [allApplications, activeTab]);

  const totalPages = Math.max(1, Math.ceil(currentApplications.length / rowsPerPage));
  const paginatedApplications = currentApplications.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const selectedHasPending = useMemo(() => currentApplications.some(app => selectedIds.has(app.id) && normalise(app.status) === "pending"), [currentApplications, selectedIds]);
  const selectedHasAccepted = useMemo(() => currentApplications.some(app => selectedIds.has(app.id) && normalise(app.status) === "accepted"), [currentApplications, selectedIds]);

  useEffect(() => {
    setSelectedIds(new Set());
    setCurrentPage(1);
  }, [activeTab]);

  useEffect(() => {
    if (selectAllRef.current) {
      const isIndeterminate = selectedIds.size > 0 && selectedIds.size < currentApplications.length;
      selectAllRef.current.indeterminate = isIndeterminate;
    }
  }, [selectedIds, currentApplications]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(new Set(currentApplications.map(app => app.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectRow = (e, id) => {
    const newSet = new Set(selectedIds);
    if (e.target.checked) {
      newSet.add(id);
    } else {
      newSet.delete(id);
    }
    setSelectedIds(newSet);
  };

  const handleMoveToInterviews = async () => {
    if (selectedIds.size === 0) return;
    try {
      const selectedApps = currentApplications.filter(app => selectedIds.has(app.id));
      for (const app of selectedApps) {
        // Create interview record
        await addDoc(collection(db, "interviews"), {
          candidateId: app.id,
          sourceType: "job",
          name: app.name || "Unknown",
          email: app.email || "",
          phone: app.phone || "",
          position: app.position || app.jobTitle || "",
          interviewDate: app.interviewDate || "",
          timeSlot: app.interviewSlot || "",
          status: "Scheduled",
          notes: "",
          rating: 0,
          assignedInterviewer: "",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          reminderSent: false,
          reminderSentAt: null,
        });
        await updateDoc(doc(db, "jobApplications", app.id), {
          status: "interviewing",
          statusUpdatedAt: serverTimestamp(),
        });
      }
      toast.success(`${selectedApps.length} candidates moved to interviews`);
      setSelectedIds(new Set());
    } catch (err) {
      toast.error("Failed to move to interviews");
      console.error(err);
    }
  };

  // ── Close dropdown on click-outside or scroll ───────────────────────────
  useEffect(() => {
    const close = () => setOpenDropdown(null);
    document.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("click", close);
      window.removeEventListener("scroll", close, true);
    };
  }, []);

  // ── Toggle portal dropdown ───────────────────────────────────────────────
  const handleToggleDropdown = (e, appId) => {
    e.stopPropagation();
    if (openDropdown === appId) {
      setOpenDropdown(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
      setOpenDropdown(appId);
    }
  };

  // ── Direct status update (for non-accept actions: reject, block, move to pending) ─
  const handleStatusUpdate = async (application, newStatus) => {
    setOpenDropdown(null);
    try {
      await updateDoc(doc(db, "jobApplications", application.id), {
        status: newStatus,
        statusUpdatedAt: serverTimestamp(),
      });
      toast.success(`Status updated to ${newStatus}`);
    } catch (err) {
      console.error("Status update failed:", err);
      toast.error("Failed to update status.");
    }
  };

  // ── Accept: move to Accepted tab only (interview is a separate step via "Move to Interviews")
  const handleConfirmAccept = async () => {
    const { application } = confirmAccept;
    if (!application) return;
    setConfirmAccept({ visible: false, application: null });
    try {
      await updateDoc(doc(db, "jobApplications", application.id), {
        status: "accepted",
        statusUpdatedAt: serverTimestamp(),
      });
      toast.success(`${application.name} has been accepted!`);
      if (sendAcceptInvite) {
        if (!application.email) {
          toast.warning("Accepted, but no email address found to send the invitation.");
          return;
        }
        await handleSendReply(application);
        toast.info("Interview invitation sent to " + application.email + ".");
      }
    } catch (err) {
      console.error("[CandidateTable] Accept failed:", err);
      toast.error("Failed to accept: " + (err?.text || err?.message || JSON.stringify(err)));
    }
  };

  // ── Bulk Accept ──────────────────────────────────────────────────────────
  const handleConfirmBulkAccept = async () => {
    setConfirmBulkAccept(false);
    const selectedApps = currentApplications.filter(app => selectedIds.has(app.id));
    if (selectedApps.length === 0) return;

    let successCount = 0;
    try {
      for (const app of selectedApps) {
        await updateDoc(doc(db, "jobApplications", app.id), {
          status: "accepted",
          statusUpdatedAt: serverTimestamp(),
        });
        successCount++;
      }
      toast.success(`Successfully accepted ${successCount} candidates!`);
      if (sendAcceptInvite) {
        let emailsSent = 0;
        for (const app of selectedApps) {
          if (app.email) {
            try { await handleSendReply(app); emailsSent++; } catch (err) {
              console.error("[CandidateTable] Bulk invite email failed for", app.email, err);
            }
          }
        }
        if (emailsSent > 0) toast.info(`Interview invitations sent to ${emailsSent} candidate${emailsSent !== 1 ? "s" : ""}.`);
      }
      setSelectedIds(new Set());
    } catch (err) {
      console.error("Bulk Accept failed:", err);
      toast.error("An error occurred during bulk accept.");
    }
  };

  // ── Bulk Reject ──────────────────────────────────────────────────────────
  const handleConfirmBulkReject = async () => {
    setConfirmBulkReject(false);
    const selectedApps = currentApplications.filter(app => selectedIds.has(app.id));
    if (selectedApps.length === 0) return;

    let successCount = 0;
    try {
      for (const app of selectedApps) {
        await updateDoc(doc(db, "jobApplications", app.id), {
          status: "rejected",
          statusUpdatedAt: serverTimestamp(),
        });
        if (sendRejectNotify && app.email) {
          await handleSendRejection(app, rejectReason);
        }
        successCount++;
      }
      toast.success(`Successfully rejected ${successCount} candidates.`);
      setSelectedIds(new Set());
    } catch (err) {
      console.error("Bulk Reject failed:", err);
      toast.error("An error occurred during bulk reject.");
    }
  };

  // ── Delete ───────────────────────────────────────────────────────────────
  const handleDeletePopup = (application) => {
    setConfirmDelete({ visible: true, application });
  };

  const confirmDeleteApplication = async () => {
    const { application } = confirmDelete;
    if (!application) return;
    setConfirmDelete({ visible: false, application: null });
    try {
      await deleteDoc(doc(db, "jobApplications", application.id));
      // Remove related calendar entries if any
      const calSnap = await getDocs(
        query(collection(db, "calendar"), where("candidateId", "==", application.id))
      );
      calSnap.forEach(async (d) => deleteDoc(doc(db, "calendar", d.id)));
      toast.success("Application deleted.");
    } catch (err) {
      console.error("Delete failed:", err);
      toast.error("Failed to delete application.");
    }
  };

  // ── Bulk Delete ──────────────────────────────────────────────────────────
  const confirmBulkDeleteApplications = async () => {
    setConfirmBulkDelete(false);
    const selectedApps = currentApplications.filter(app => selectedIds.has(app.id));
    if (selectedApps.length === 0) return;

    let successCount = 0;
    try {
      for (const app of selectedApps) {
        await deleteDoc(doc(db, "jobApplications", app.id));
        const calSnap = await getDocs(
          query(collection(db, "calendar"), where("candidateId", "==", app.id))
        );
        calSnap.forEach(async (d) => deleteDoc(doc(db, "calendar", d.id)));
        successCount++;
      }
      toast.success(`Successfully deleted ${successCount} applications.`);
      setSelectedIds(new Set());
    } catch (err) {
      console.error("Bulk Delete failed:", err);
      toast.error("An error occurred during bulk delete.");
    }
  };

  // ── View Resume ──────────────────────────────────────────────────────────
  const handleViewResume = (application) => {
    if (!application.resumeURL) {
      toast.error("No resume found for this applicant!");
      return;
    }
    setViewResume({ visible: true, application });
  };

  const getFileType = (url) => {
    if (!url) return "";
    return url.split(".").pop().toLowerCase();
  };

  if (loading) return <LoadingAnimation text="Loading Applications..." />;

  return (
    <div className="candidate-table-container" style={{ marginTop: "20px" }}>
      {selectedIds.size > 0 && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#fff", padding: "12px 16px", borderRadius: "12px", border: "1px solid #e2e8f0", marginBottom: "20px", boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
          <span style={{ fontWeight: "600", fontSize: "14px", color: "#4a5568" }}>{selectedIds.size} Candidates Selected</span>
          <div style={{ display: "flex", gap: "10px" }}>
            {(activeTab === "pending" || (activeTab === "all" && selectedHasPending)) && (
              <>
                <button onClick={() => setConfirmBulkAccept(true)} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 16px", background: "#38a169", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}>
                  <i className="fas fa-check"></i> Accept
                </button>
                <button onClick={() => setConfirmBulkReject(true)} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 16px", background: "#e53e3e", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}>
                  <i className="fas fa-times"></i> Reject
                </button>
              </>
            )}
            {(activeTab === "accepted" || (activeTab === "all" && selectedHasAccepted)) && (
              <button onClick={handleMoveToInterviews} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 16px", background: "#3182ce", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}>
                <i className="fas fa-arrow-right"></i> Move To Interviews
              </button>
            )}
            <button onClick={() => setConfirmBulkDelete(true)} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 16px", background: "#eb3f21ff", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}>
              <i className="fas fa-trash-alt"></i> Delete
            </button>
          </div>
        </div>
      )}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%" }}>
          <thead>
            <tr>
              <th style={{ width: "40px" }}>
                <input
                  ref={selectAllRef}
                  type="checkbox"
                  className="oval-checkbox"
                  checked={currentApplications.length > 0 && selectedIds.size === currentApplications.length}
                  onChange={handleSelectAll}
                />
              </th>
              <th>Name</th>
              <th>Email</th>
              <th>CNIC</th>
              <th>Phone</th>
              <th>Job Title</th>
              <th>Interview Date</th>
              <th>Time Slot</th>
              <th>Status</th>
              <th>Applied On</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentApplications.length > 0 ? (
              paginatedApplications.map((application) => {
                const appliedOn = application?.createdAt
                  ? formatNiceDate(application.createdAt)
                  : "N/A";

                const statusKey = normalise(application.status);
                const statusCfg = STATUS_CONFIG[statusKey];

                const dropdownOptions = statusKey === "pending"
                  ? [
                    { label: "View Resume", icon: "fas fa-eye", color: "#2D3748", onClick: () => { handleViewResume(application); setOpenDropdown(null); } },
                    { label: "Accept", icon: "fas fa-check", color: "#21b11d", onClick: () => { setConfirmAccept({ visible: true, application }); setOpenDropdown(null); } },
                    { label: "Reject", icon: "fas fa-times", color: "#E53E3E", onClick: () => { setConfirmReject({ visible: true, application }); setOpenDropdown(null); } },
                    { label: "Block", icon: "fas fa-ban", color: "#718096", onClick: () => handleStatusUpdate(application, "blocked") },
                    { label: "Delete", icon: "fas fa-trash", color: "#E53E3E", divider: true, onClick: () => { handleDeletePopup(application); setOpenDropdown(null); } },
                  ]
                  : [
                    { label: "View Resume", icon: "fas fa-eye", color: "#2D3748", onClick: () => { handleViewResume(application); setOpenDropdown(null); } },
                    { label: "Move to Pending", icon: "fas fa-undo", color: "#FF8C00", onClick: () => handleStatusUpdate(application, "pending") },
                    { label: "Delete", icon: "fas fa-trash", color: "#E53E3E", divider: true, onClick: () => { handleDeletePopup(application); setOpenDropdown(null); } },
                  ];

                return (
                  <tr key={application.id}>
                    <td>
                      <input
                        type="checkbox"
                        className="oval-checkbox"
                        checked={selectedIds.has(application.id)}
                        onChange={(e) => handleSelectRow(e, application.id)}
                      />
                    </td>
                    <td>{application.name || "No Name"}</td>
                    <td>{application.email || "No Email"}</td>
                    <td>{application.cnic || "No CNIC"}</td>
                    <td>{application.phone || "No Phone"}</td>
                    <td>
                      <span
                        style={{
                          backgroundColor: "#FF8000",
                          color: "white",
                          padding: "4px 8px",
                          borderRadius: "12px",
                          fontSize: "12px",
                          display: "inline-block",
                        }}
                      >
                        {application.position || application.jobTitle || "N/A"}
                      </span>
                    </td>
                    <td>{application.interviewDate ? formatNiceDate(application.interviewDate) : "N/A"}</td>
                    <td>{application.interviewSlot || "—"}</td>
                    <td>
                      <p
                        className="status_chip"
                        style={{
                          backgroundColor: statusCfg.bg,
                          color: statusCfg.color,
                          padding: "4px 12px",
                          borderRadius: "20px",
                          fontSize: "12px",
                          display: "inline-block",
                          margin: 0,
                          fontWeight: 600,
                          textTransform: "capitalize",
                        }}
                      >
                        {statusCfg.label}
                      </p>
                    </td>
                    <td>{appliedOn}</td>
                    <td style={{ textAlign: "center" }}>
                      <button
                        className="action-button"
                        title="More actions"
                        onClick={(e) => handleToggleDropdown(e, application.id)}
                        style={{
                          background: "transparent",
                          border: "1px solid #e2e8f0",
                          borderRadius: "8px",
                          padding: "6px 12px",
                          cursor: "pointer",
                          fontSize: "20px",
                          fontWeight: "bold",
                          color: "#4A5568",
                          lineHeight: 1,
                          transition: "background 0.15s",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#f7f8fa")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        &#8942;
                      </button>

                      {/* React Portal Dropdown */}
                      {openDropdown === application.id && createPortal(
                        <div
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            position: "fixed",
                            top: `${dropdownPos.top}px`,
                            right: `${dropdownPos.right}px`,
                            background: "#ffffff",
                            border: "1px solid #E2E8F0",
                            borderRadius: "12px",
                            boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                            zIndex: 999999,
                            minWidth: "160px",
                            overflow: "hidden",
                          }}
                        >
                          {dropdownOptions.map((item, idx) => (
                            <React.Fragment key={idx}>
                              {item.divider && (
                                <div style={{ borderTop: "1px solid #E2E8F0", margin: "2px 0" }} />
                              )}
                              <button
                                onClick={item.onClick}
                                style={{
                                  width: "100%",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "10px",
                                  padding: "10px 16px",
                                  background: "transparent",
                                  border: "none",
                                  cursor: "pointer",
                                  color: item.color,
                                  fontSize: "13px",
                                  fontWeight: "500",
                                  textAlign: "left",
                                  transition: "background 0.12s",
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.background = "#f7f8fa")}
                                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                              >
                                <i className={item.icon} style={{ width: "14px", textAlign: "center", flexShrink: 0 }}></i>
                                {item.label}
                              </button>
                            </React.Fragment>
                          ))}
                        </div>,
                        document.body
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="11" style={{ textAlign: "center" }}>
                  No applications found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Bar */}
      {currentApplications.length > 0 && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 4px 4px", flexWrap: "wrap", gap: "12px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "#4a5568", whiteSpace: "nowrap" }}>
            <span style={{ lineHeight: "1", verticalAlign: "middle" }}>Rows per page:</span>
            <select
              value={rowsPerPage}
              onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
              style={{ padding: "2px 5px", border: "1px solid #e2e8f0", borderRadius: "6px", fontSize: "13px", background: "#fff", cursor: "pointer", width: "72px", lineHeight: "1", verticalAlign: "middle" }}
            >
              {[5, 10, 25, 50].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "#4a5568" }}>
            <span>
              {(currentPage - 1) * rowsPerPage + 1}–{Math.min(currentPage * rowsPerPage, currentApplications.length)} of {currentApplications.length}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              style={{ padding: "6px 14px", border: "1px solid #e2e8f0", borderRadius: "6px", background: currentPage === 1 ? "#f7f8fa" : "#fff", color: currentPage === 1 ? "#cbd5e0" : "#4a5568", cursor: currentPage === 1 ? "not-allowed" : "pointer", fontSize: "13px", fontWeight: "500" }}
            >
              &#8249; Prev
            </button>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              style={{ padding: "6px 14px", border: "1px solid #e2e8f0", borderRadius: "6px", background: currentPage === totalPages ? "#f7f8fa" : "#fff", color: currentPage === totalPages ? "#cbd5e0" : "#4a5568", cursor: currentPage === totalPages ? "not-allowed" : "pointer", fontSize: "13px", fontWeight: "500" }}
            >
              Next &#8250;
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDelete.visible && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 9999, backdropFilter: "blur(4px)",
          }}
        >
          <div
            style={{
              background: "white", padding: "32px", borderRadius: "16px",
              textAlign: "center", width: "360px",
              boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
            }}
          >
            <div style={{
              width: "52px", height: "52px",
              background: "linear-gradient(135deg, #f56565 0%, #ed8936 100%)",
              borderRadius: "50%", display: "flex", alignItems: "center",
              justifyContent: "center", margin: "0 auto 16px",
            }}>
              <span style={{ color: "white", fontSize: "20px", fontWeight: "bold" }}>!</span>
            </div>
            <h3 style={{ margin: "0 0 12px 0", color: "#2D3748", fontSize: "18px" }}>Confirm Delete</h3>
            <p style={{ margin: "0 0 28px 0", color: "#718096", fontSize: "14px", lineHeight: "1.4" }}>
              Delete <strong style={{ color: "#2D3748" }}>{confirmDelete.application?.name}</strong>?{" "}
              This action cannot be undone.
            </p>
            <div style={{ display: "flex", gap: "12px" }}>
              <button
                onClick={() => setConfirmDelete({ visible: false, application: null })}
                style={{
                  flex: 1, padding: "12px 24px", border: "1px solid #e2e8f0",
                  borderRadius: "10px", cursor: "pointer", background: "transparent", color: "#718096",
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteApplication}
                style={{
                  flex: 1, padding: "12px 24px", border: "none", borderRadius: "10px",
                  cursor: "pointer", fontWeight: "600", color: "white",
                  background: "linear-gradient(135deg, #f56565 0%, #e53e3e 100%)",
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Accept Confirmation Modal ── */}
      {confirmAccept.visible && (
        <div
          style={{
            position: "fixed", inset: 0,
            background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 10000,
          }}
          onClick={() => setConfirmAccept({ visible: false, application: null })}
        >
          <div
            style={{
              background: "#fff", borderRadius: "18px", padding: "36px 32px",
              width: "100%", maxWidth: "420px", textAlign: "center",
              boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Icon */}
            <div style={{
              width: "64px", height: "64px", borderRadius: "50%",
              background: "linear-gradient(135deg, #38a169, #21b11d)",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 20px", boxShadow: "0 4px 16px rgba(33,177,29,0.3)",
            }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="white">
                <path d="m9 16.17l-4.17-4.17l-1.42 1.41L9 19L21 7l-1.41-1.41z" />
              </svg>
            </div>

            <h3 style={{ margin: "0 0 8px", fontSize: "20px", fontWeight: "700", color: "#1a202c" }}>
              Accept Application?
            </h3>
            <p style={{ margin: "0 0 6px", fontSize: "15px", color: "#4a5568", lineHeight: "1.6" }}>
              You are about to accept <strong style={{ color: "#1a202c" }}>{confirmAccept.application?.name}</strong>
            </p>
            <p style={{ margin: "0 0 16px", fontSize: "13px", color: "#718096", lineHeight: "1.6" }}>
              Position: <strong>{confirmAccept.application?.position || confirmAccept.application?.jobTitle || "N/A"}</strong>
              <br />
              Interview: <strong>{confirmAccept.application?.interviewDate ? formatNiceDate(confirmAccept.application.interviewDate) : "TBD"}</strong> at{" "}
              <strong>{confirmAccept.application?.interviewSlot || "TBD"}</strong>
            </p>

            <div
              onClick={() => setSendAcceptInvite(!sendAcceptInvite)}
              style={{ display: "flex", alignItems: "flex-start", gap: "12px", marginBottom: "20px", padding: "12px 16px", border: "1.5px solid " + (sendAcceptInvite ? "#38a169" : "#e2e8f0"), borderRadius: "12px", backgroundColor: sendAcceptInvite ? "#f0fff4" : "#f8fafc", cursor: "pointer", textAlign: "left", transition: "all 0.2s ease" }}
            >
              <input type="checkbox" checked={sendAcceptInvite} onChange={(e) => { e.stopPropagation(); setSendAcceptInvite(e.target.checked); }} style={{ cursor: "pointer", marginTop: "4px" }} />
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span style={{ fontSize: "14px", fontWeight: "700", color: "#2d3748" }}>Send Email Invitation</span>
                <span style={{ fontSize: "12px", color: "#718096", marginTop: "2px", lineHeight: "1.4" }}>Send an interview invitation email to <strong>{confirmAccept.application?.email || "candidate"}</strong>.</span>
              </div>
            </div>

            <div style={{ display: "flex", gap: "12px" }}>
              <button
                onClick={() => setConfirmAccept({ visible: false, application: null })}
                style={{
                  flex: 1, padding: "12px", border: "1.5px solid #e2e8f0",
                  borderRadius: "10px", cursor: "pointer", background: "#fff",
                  color: "#718096", fontWeight: "600", fontSize: "14px",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = "#f7f8fa"}
                onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmAccept}
                style={{
                  flex: 1, padding: "12px", border: "none",
                  borderRadius: "10px", cursor: "pointer",
                  background: "linear-gradient(135deg, #38a169, #21b11d)",
                  color: "#fff", fontWeight: "700", fontSize: "14px",
                  boxShadow: "0 4px 12px rgba(33,177,29,0.3)",
                  transition: "opacity 0.15s",
                }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = "0.88"}
                onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
              >
                Accept
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reject Confirmation Modal ── */}
      {confirmReject.visible && (
        <div
          style={{
            position: "fixed", inset: 0,
            background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 10000,
          }}
          onClick={() => setConfirmReject({ visible: false, application: null })}
        >
          <div
            style={{
              background: "#fff", borderRadius: "18px", padding: "36px 32px",
              width: "100%", maxWidth: "420px", textAlign: "center",
              boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Icon */}
            <div style={{
              width: "64px", height: "64px", borderRadius: "50%",
              background: "linear-gradient(135deg, #718096, #4a5568)",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 20px", boxShadow: "0 4px 16px rgba(74,85,104,0.3)",
            }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="white">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            </div>

            <h3 style={{ margin: "0 0 8px", fontSize: "20px", fontWeight: "700", color: "#1a202c" }}>
              Reject Application?
            </h3>
            <p style={{ margin: "0 0 6px", fontSize: "15px", color: "#4a5568", lineHeight: "1.6" }}>
              You are about to reject <strong style={{ color: "#1a202c" }}>{confirmReject.application?.name}</strong>
            </p>
            <p style={{ margin: "0 0 28px", fontSize: "13px", color: "#718096", lineHeight: "1.6" }}>
              Position: <strong>{confirmReject.application?.position || confirmReject.application?.jobTitle || "N/A"}</strong>
            </p>

            <div style={{ display: "flex", gap: "12px" }}>
              <button
                onClick={() => setConfirmReject({ visible: false, application: null })}
                style={{
                  flex: 1, padding: "12px", border: "1.5px solid #e2e8f0",
                  borderRadius: "10px", cursor: "pointer", background: "#fff",
                  color: "#718096", fontWeight: "600", fontSize: "14px",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = "#f7f8fa"}
                onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmReject}
                style={{
                  flex: 1, padding: "12px", border: "none",
                  borderRadius: "10px", cursor: "pointer",
                  background: "linear-gradient(135deg, #718096, #4a5568)",
                  color: "#fff", fontWeight: "700", fontSize: "14px",
                  boxShadow: "0 4px 12px rgba(74,85,104,0.35)",
                  transition: "opacity 0.15s",
                }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = "0.88"}
                onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
              >
                Reject
              </button>
            </div>

            <div
              onClick={() => setSendRejectNotify(!sendRejectNotify)}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "12px",
                marginTop: "20px",
                padding: "12px 16px",
                border: "1.5px solid " + (sendRejectNotify ? "#e53e3e" : "#e2e8f0"),
                borderRadius: "12px",
                backgroundColor: sendRejectNotify ? "#fff5f5" : "#f8fafc",
                cursor: "pointer",
                textAlign: "left",
                transition: "all 0.2s ease",
              }}
            >
              <input
                type="checkbox"
                id="sendNotifyCheckbox"
                checked={sendRejectNotify}
                onChange={(e) => {
                  e.stopPropagation();
                  setSendRejectNotify(e.target.checked);
                }}
                style={{ cursor: "pointer", marginTop: "4px" }}
              />
              <div style={{ display: "flex", flexDirection: "column" }}>
                <label htmlFor="sendNotifyCheckbox" style={{ fontSize: "14px", fontWeight: "700", color: "#2d3748", cursor: "pointer" }}>
                  Send Rejection Email
                </label>
                <span style={{ fontSize: "12px", color: "#718096", marginTop: "2px", lineHeight: "1.4" }}>
                  Send a polite rejection notification to <strong>{confirmReject.application?.email || "candidate"}</strong>.
                </span>
              </div>
            </div>

            {/* Rejection reason selector */}
            {sendRejectNotify && (
              <div style={{ marginTop: "12px", textAlign: "left" }}>
                <p style={{ margin: "0 0 8px", fontSize: "12px", fontWeight: "700", color: "#718096", textTransform: "uppercase", letterSpacing: "0.5px" }}>Rejection Reason</p>
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {[
                    { value: "capacity", label: "Capacity Limit", desc: "All available seats for this role have been filled." },
                    { value: "experience", label: "Irrelevant Experience", desc: "Candidate's background doesn't align with the role requirements." },
                  ].map(opt => (
                    <div
                      key={opt.value}
                      onClick={() => setRejectReason(opt.value)}
                      style={{ display: "flex", alignItems: "flex-start", gap: "10px", padding: "10px 14px", border: "1.5px solid " + (rejectReason === opt.value ? "#e53e3e" : "#e2e8f0"), borderRadius: "10px", backgroundColor: rejectReason === opt.value ? "#fff5f5" : "#fafafa", cursor: "pointer", transition: "all 0.2s ease" }}
                    >
                      <input
                        type="radio"
                        name="rejectReason"
                        checked={rejectReason === opt.value}
                        onChange={() => setRejectReason(opt.value)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ marginTop: "3px", accentColor: "#e53e3e", cursor: "pointer" }}
                      />
                      <div>
                        <p style={{ margin: 0, fontSize: "13px", fontWeight: "700", color: "#2d3748" }}>{opt.label}</p>
                        <p style={{ margin: "2px 0 0", fontSize: "12px", color: "#718096", lineHeight: "1.4" }}>{opt.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* View Resume Modal */}
      {viewResume.visible && (
        <div
          style={{
            position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
            background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center",
            justifyContent: "center", zIndex: 9999, padding: "10px", boxSizing: "border-box",
          }}
          onClick={() => setViewResume({ visible: false, application: null })}
        >
          <div
            style={{
              background: "#fff", borderRadius: "14px", width: "90vw", maxWidth: "1200px",
              height: "85vh", display: "flex", flexDirection: "column",
              overflow: "hidden", border: "1px solid #e5e7eb",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              padding: "12px 16px", borderBottom: "1px solid #e5e7eb",
              display: "flex", flexDirection: "row", flexWrap: "wrap",
              justifyContent: "space-between", alignItems: "center",
              background: "#fafafa", flexShrink: 0, gap: "12px",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: "0", flex: "1" }}>
                <div style={{
                  width: "34px", height: "34px", borderRadius: "50%",
                  background: "#f3f4f6", border: "1px solid #e5e7eb",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontWeight: "600", fontSize: "13px", color: "#374151", flexShrink: 0,
                }}>
                  {viewResume.application?.name?.charAt(0)?.toUpperCase()}
                </div>
                <div style={{ minWidth: "0", flex: "1" }}>
                  <p style={{ margin: 0, fontWeight: "600", fontSize: "clamp(12px, 4vw, 14px)", color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {viewResume.application?.name}
                  </p>
                  <p style={{ margin: 0, fontSize: "10px", color: "#9ca3af" }}>Resume</p>
                </div>
              </div>
              <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
                <a href={viewResume.application?.resumeURL} download style={{ color: "#374151", padding: "6px 12px", border: "1px solid #e5e7eb", borderRadius: "8px", textDecoration: "none", fontSize: "12px", fontWeight: "500", background: "#fff", display: "inline-flex", alignItems: "center", gap: "4px" }}>
                  📥 Download
                </a>
                <a href={viewResume.application?.resumeURL} target="_blank" rel="noopener noreferrer" style={{ color: "#374151", padding: "6px 12px", border: "1px solid #e5e7eb", borderRadius: "8px", textDecoration: "none", fontSize: "12px", fontWeight: "500", background: "#fff", display: "inline-flex", alignItems: "center", gap: "4px" }}>
                  🔗 Open
                </a>
                <button onClick={() => setViewResume({ visible: false, application: null })} style={{ background: "#f3f4f6", color: "#374151", border: "none", padding: "6px 12px", borderRadius: "8px", cursor: "pointer", fontSize: "12px", fontWeight: "500" }}>
                  ✕ Close
                </button>
              </div>
            </div>

            {/* Content */}
            <div style={{ flex: 1, background: "#f9fafb", overflow: "hidden" }}>
              {getFileType(viewResume.application?.resumeURL) === "pdf" ? (
                <iframe src={viewResume.application?.resumeURL} title="Resume" width="100%" height="100%" style={{ border: "none", display: "block" }} />
              ) : (
                <div style={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "12px", color: "#6b7280", padding: "20px", textAlign: "center" }}>
                  <div style={{ width: "48px", height: "48px", background: "#f3f4f6", border: "1px solid #e5e7eb", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "20px" }}>📄</div>
                  <p style={{ margin: 0, fontWeight: "500", color: "#374151", fontSize: "14px" }}>Preview not available</p>
                  <p style={{ margin: 0, fontSize: "12px", color: "#9ca3af" }}>This file type can't be previewed in the browser.</p>
                  <a href={viewResume.application?.resumeURL} target="_blank" rel="noopener noreferrer" style={{ marginTop: "4px", color: "#374151", fontSize: "13px", fontWeight: "500", padding: "8px 20px", border: "1px solid #d1d5db", borderRadius: "8px", textDecoration: "none", background: "#fff" }}>
                    Open in new tab ↗
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* ── Bulk Accept Modal ── */}
      {confirmBulkAccept && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000 }}>
          <div style={{ background: "#fff", borderRadius: "18px", padding: "36px 32px", width: "100%", maxWidth: "460px", textAlign: "center", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
            <div style={{ width: "64px", height: "64px", borderRadius: "50%", background: "linear-gradient(135deg, #38a169, #21b11d)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", boxShadow: "0 4px 16px rgba(33,177,29,0.3)" }}>
              <i className="fas fa-check" style={{ color: "white", fontSize: "28px" }}></i>
            </div>
            <h3 style={{ margin: "0 0 8px", fontSize: "20px", fontWeight: "700", color: "#1a202c" }}>Bulk Accept</h3>
            <p style={{ margin: "0 0 16px", fontSize: "15px", color: "#4a5568", lineHeight: "1.6" }}>
              You are about to accept <strong>{selectedIds.size} candidates</strong>.
            </p>
            <div style={{ maxHeight: "150px", overflowY: "auto", background: "#f7fafc", padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0", textAlign: "left", marginBottom: "16px" }}>
              {currentApplications.filter(app => selectedIds.has(app.id)).map(app => (
                <div key={app.id} style={{ fontSize: "13px", color: "#4a5568", marginBottom: "4px" }}>
                  <strong>{app.name}</strong> - {app.email}
                </div>
              ))}
            </div>
            <div onClick={() => setSendAcceptInvite(!sendAcceptInvite)} style={{ display: "flex", alignItems: "flex-start", gap: "12px", marginBottom: "16px", padding: "12px 16px", border: "1.5px solid " + (sendAcceptInvite ? "#38a169" : "#e2e8f0"), borderRadius: "12px", backgroundColor: sendAcceptInvite ? "#f0fff4" : "#f8fafc", cursor: "pointer", textAlign: "left", transition: "all 0.2s ease" }}>
              <input type="checkbox" className="oval-checkbox" checked={sendAcceptInvite} onChange={(e) => { e.stopPropagation(); setSendAcceptInvite(e.target.checked); }} style={{ marginTop: "4px" }} />
              <div style={{ display: "flex", flexDirection: "column" }}>
                <label style={{ fontSize: "14px", fontWeight: "700", color: "#2d3748", cursor: "pointer" }}>Send Email Invitations</label>
                <span style={{ fontSize: "12px", color: "#718096", marginTop: "2px", lineHeight: "1.4" }}>Send interview invitation emails to all selected candidates.</span>
              </div>
            </div>
            <div style={{ display: "flex", gap: "12px" }}>
              <button onClick={() => setConfirmBulkAccept(false)} style={{ flex: 1, padding: "12px", border: "1.5px solid #e2e8f0", borderRadius: "10px", cursor: "pointer", background: "#fff", color: "#718096", fontWeight: "600", fontSize: "14px" }}>Cancel</button>
              <button onClick={handleConfirmBulkAccept} style={{ flex: 1, padding: "12px", border: "none", borderRadius: "10px", cursor: "pointer", background: "linear-gradient(135deg, #38a169, #21b11d)", color: "#fff", fontWeight: "700", fontSize: "14px" }}>Accept All</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Bulk Reject Modal ── */}
      {confirmBulkReject && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000 }}>
          <div style={{ background: "#fff", borderRadius: "18px", padding: "36px 32px", width: "100%", maxWidth: "460px", textAlign: "center", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
            <div style={{ width: "64px", height: "64px", borderRadius: "50%", background: "linear-gradient(135deg, #718096, #4a5568)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", boxShadow: "0 4px 16px rgba(74,85,104,0.3)" }}>
              <i className="fas fa-times" style={{ color: "white", fontSize: "28px" }}></i>
            </div>
            <h3 style={{ margin: "0 0 8px", fontSize: "20px", fontWeight: "700", color: "#1a202c" }}>Bulk Reject</h3>
            <p style={{ margin: "0 0 16px", fontSize: "15px", color: "#4a5568", lineHeight: "1.6" }}>
              You are about to reject <strong>{selectedIds.size} candidates</strong>.
            </p>
            <div style={{ maxHeight: "150px", overflowY: "auto", background: "#f7fafc", padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0", textAlign: "left", marginBottom: "20px" }}>
              {currentApplications.filter(app => selectedIds.has(app.id)).map(app => (
                <div key={app.id} style={{ fontSize: "13px", color: "#4a5568", marginBottom: "4px" }}>
                  <strong>{app.name}</strong> - {app.email}
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: "12px", marginBottom: "16px" }}>
              <button onClick={() => setConfirmBulkReject(false)} style={{ flex: 1, padding: "12px", border: "1.5px solid #e2e8f0", borderRadius: "10px", cursor: "pointer", background: "#fff", color: "#718096", fontWeight: "600", fontSize: "14px" }}>Cancel</button>
              <button onClick={handleConfirmBulkReject} style={{ flex: 1, padding: "12px", border: "none", borderRadius: "10px", cursor: "pointer", background: "linear-gradient(135deg, #718096, #4a5568)", color: "#fff", fontWeight: "700", fontSize: "14px" }}>Reject All</button>
            </div>
            <div onClick={() => setSendRejectNotify(!sendRejectNotify)} style={{ display: "flex", alignItems: "flex-start", gap: "12px", padding: "12px 16px", border: "1.5px solid " + (sendRejectNotify ? "#e53e3e" : "#e2e8f0"), borderRadius: "12px", backgroundColor: sendRejectNotify ? "#fff5f5" : "#f8fafc", cursor: "pointer", textAlign: "left", transition: "all 0.2s ease" }}>
              <input type="checkbox" className="oval-checkbox" checked={sendRejectNotify} onChange={(e) => { e.stopPropagation(); setSendRejectNotify(e.target.checked); }} style={{ marginTop: "4px" }} />
              <div style={{ display: "flex", flexDirection: "column" }}>
                <label style={{ fontSize: "14px", fontWeight: "700", color: "#2d3748", cursor: "pointer" }}>Send Rejection Emails</label>
                <span style={{ fontSize: "12px", color: "#718096", marginTop: "2px", lineHeight: "1.4" }}>Send polite rejection notifications to all selected candidates.</span>
              </div>
            </div>

            {sendRejectNotify && (
              <div style={{ marginTop: "12px", textAlign: "left" }}>
                <p style={{ margin: "0 0 8px", fontSize: "12px", fontWeight: "700", color: "#718096", textTransform: "uppercase", letterSpacing: "0.5px" }}>Rejection Reason</p>
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {[
                    { value: "capacity", label: "Capacity Limit", desc: "All available seats for this role have been filled." },
                    { value: "experience", label: "Irrelevant Experience", desc: "Candidates' background doesn't align with the role requirements." },
                  ].map(opt => (
                    <div
                      key={opt.value}
                      onClick={() => setRejectReason(opt.value)}
                      style={{ display: "flex", alignItems: "flex-start", gap: "10px", padding: "10px 14px", border: "1.5px solid " + (rejectReason === opt.value ? "#e53e3e" : "#e2e8f0"), borderRadius: "10px", backgroundColor: rejectReason === opt.value ? "#fff5f5" : "#fafafa", cursor: "pointer", transition: "all 0.2s ease" }}
                    >
                      <input
                        type="radio"
                        name="bulkRejectReason"
                        checked={rejectReason === opt.value}
                        onChange={() => setRejectReason(opt.value)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ marginTop: "3px", accentColor: "#e53e3e", cursor: "pointer" }}
                      />
                      <div>
                        <p style={{ margin: 0, fontSize: "13px", fontWeight: "700", color: "#2d3748" }}>{opt.label}</p>
                        <p style={{ margin: "2px 0 0", fontSize: "12px", color: "#718096", lineHeight: "1.4" }}>{opt.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Bulk Delete Modal ── */}
      {confirmBulkDelete && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000 }}>
          <div style={{ background: "#fff", borderRadius: "18px", padding: "36px 32px", width: "100%", maxWidth: "460px", textAlign: "center", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
            <div style={{ width: "64px", height: "64px", borderRadius: "50%", background: "linear-gradient(135deg, #f56565 0%, #e53e3e 100%)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", boxShadow: "0 4px 16px rgba(229,62,62,0.3)" }}>
              <i className="fas fa-trash-alt" style={{ color: "white", fontSize: "28px" }}></i>
            </div>
            <h3 style={{ margin: "0 0 8px", fontSize: "20px", fontWeight: "700", color: "#1a202c" }}>Bulk Delete</h3>
            <p style={{ margin: "0 0 16px", fontSize: "15px", color: "#4a5568", lineHeight: "1.6" }}>
              You are about to permanently delete <strong>{selectedIds.size} candidates</strong>. This action cannot be undone.
            </p>
            <div style={{ maxHeight: "150px", overflowY: "auto", background: "#fff5f5", padding: "12px", borderRadius: "8px", border: "1px solid #fed7d7", textAlign: "left", marginBottom: "20px" }}>
              {currentApplications.filter(app => selectedIds.has(app.id)).map(app => (
                <div key={app.id} style={{ fontSize: "13px", color: "#c53030", marginBottom: "4px" }}>
                  <strong>{app.name}</strong> - {app.email}
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: "12px" }}>
              <button onClick={() => setConfirmBulkDelete(false)} style={{ flex: 1, padding: "12px", border: "1.5px solid #e2e8f0", borderRadius: "10px", cursor: "pointer", background: "#fff", color: "#718096", fontWeight: "600", fontSize: "14px" }}>Cancel</button>
              <button onClick={confirmBulkDeleteApplications} style={{ flex: 1, padding: "12px", border: "none", borderRadius: "10px", cursor: "pointer", background: "linear-gradient(135deg, #f56565 0%, #e53e3e 100%)", color: "white", fontWeight: "600" }}>Delete All</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CandidateTable;